import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
  RefObject,
} from 'react';
import {
  useForm,
} from 'react-hook-form';
import isNil from 'lodash/isNil';
import Divider from '@mui/material/Divider';
import {
  InsuranceDTO,
} from 'dtos';
import EditIcon from 'core/icons/edit';
import Controller from 'core/components/FormController';
import {
  appointmentsEventChannel,
  billingEventChannel,
  visitNoteEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useGetPatientInsurances,
} from 'views/EMR/components/Insurance/hooks';
import {
  useModifyAppointmentPaymentType,
} from 'pages/Dashboard/pages/Appointments/pages/List/hooks';
import clsx from 'clsx';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import Spinner from 'pages/Dashboard/components/Spinner';
import Popover from 'core/components/Popover';
import {
  ModifyAppointmentPaymentTypeRequestDTO,
} from 'dtos/modifyAppointmentPaymentTypeRequestDTO';
import PatientInsuranceSelect from 'pages/Dashboard/components/PatientInsuranceSelect';
import OutOfPocketCheckbox from 'views/EMR/components/OutOfPocketCheckbox';
import ToolTip from 'core/components/Tooltip';
import defer from 'lodash/defer';
import {
  formatMoney,
  isNilOrZero,
} from '@populateui/utils';

type Parts = 'paper';

type Props = {
  patientId?: number;
  encounterId?: number;
  isOutOfPocket?: boolean;
  readonly?: boolean;
  insuranceId?: number;
  isLoading?: boolean;
  container?: HTMLElement | null;
  onClose?: () => void;
  alwaysEditable?: boolean;
  classes?: Partial<Record<Parts, string>>;
  shiftedToPatient?: boolean;
};

type FormData = ModifyAppointmentPaymentTypeRequestDTO & {
  insurance?: InsuranceDTO | null;
};

type InsuranceNameProps = Partial<InsuranceDTO> & {
  className?: string;
};

export function InsuranceName({
  insuranceName = '',
  className,
}: InsuranceDTO & InsuranceNameProps) {
  const insuranceNameRef = useRef<HTMLDivElement>(null) as RefObject<HTMLDivElement>;

  return (
    <ToolTip
      title={insuranceName}
      placement="top"
      ref={insuranceNameRef}
      auto
    >
      <div
        className={clsx('truncate max-w-[280px]', className)}
        ref={insuranceNameRef}
      >
        {insuranceName}
      </div>
    </ToolTip>
  );
}

export default function EditableAppointmentInsurance({
  patientId,
  encounterId,
  isOutOfPocket,
  readonly = false,
  insuranceId,
  isLoading,
  container: _container,
  onClose,
  alwaysEditable = false,
  shiftedToPatient = false,
  classes,
}: Props) {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);

  const ref = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setContainer(_container ?? null);
  }, [_container]);

  const {
    data: insurances,
    refetch: refetchInsurance,
    isLoading: loadingInsurances,
  } = useGetPatientInsurances(patientId);

  const selectedInsurance = useMemo(
    () => insurances?.find(({ insuranceId: id }) => insuranceId === id),
    [insurances, insuranceId],
  );

  const formProps = useForm<FormData>({
    mode: 'onChange',
    defaultValues: {
      isOutOfPocket,
      insurance: selectedInsurance,
    },
  });

  const {
    control,
    setValue,
    handleSubmit,
    reset,
  } = formProps;

  const resetForm = useCallback(() => {
    reset({
      isOutOfPocket,
      insurance: selectedInsurance,
    });
  }, [isOutOfPocket, selectedInsurance]);

  useEffect(() => {
    resetForm();
  }, [isOutOfPocket, selectedInsurance]);

  const {
    isPending: modifyingAppointment,
    mutateAsync: modifyAppointmentPaymentType,
  } = useModifyAppointmentPaymentType();

  const hasInsurance = !isNil(selectedInsurance);

  const updateData = async ({
    insuranceId,
    isOutOfPocket,
  }: ModifyAppointmentPaymentTypeRequestDTO) => {
    try {
      setIsUpdating(true);

      await modifyAppointmentPaymentType({
        isOutOfPocket,
        insuranceId: isNilOrZero(insuranceId) ? null : insuranceId,
        encounterId,
      });

      await refetchInsurance();
      appointmentsEventChannel.emit('onEventUpdated', encounterId);
      visitNoteEventChannel.emit('onRelatedInfoUpdate', { patientId: patientId ?? 0 });
    } catch (e) {
      console.error(e);
      resetForm();
    } finally {
      setIsUpdating(false);
    }
  };

  const onUpdate = async ({ isOutOfPocket, insurance }: FormData) => {
    try {
      setContainer(null);
      const insuranceId = isOutOfPocket ? 0 : insurance?.insuranceId;
      await updateData({
        isOutOfPocket,
        insuranceId,
      });
      billingEventChannel.emit('onChargesInfoChange', { encounterId, isOutOfPocket });

      defer(() => {
        buttonRef?.current?.focus();
      });
    } catch (e) {
      console.error(e);
    } finally {
      onClose?.();
    }
  };

  return (
    <>
      {!alwaysEditable && (
        <div className="flex items-center gap-1" ref={ref}>
          {isUpdating ? (
            <>
              <Spinner />
              <span>Updating..</span>
            </>
          ) : (
            <div className="font-semibold">
              {loadingInsurances && <Spinner />}
              {!loadingInsurances && (
                <div
                  role="button"
                  tabIndex={0}
                  ref={buttonRef}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setContainer(ref?.current);
                    }
                  }}
                  className="flex items-center gap-1 cursor-pointer rounded p-1 hover:shadow hover:shadow-gray-200 hover:bg-white"
                  onClick={() => { setContainer(ref?.current); }}
                >
                  {hasInsurance && (
                    <div className="flex items-center gap-2">
                      <InsuranceName {...selectedInsurance} />
                      <div className="font-normal text-xs">
                        {!isNilOrZero(selectedInsurance.copay)
                          ? (
                            <>
                              {formatMoney(selectedInsurance?.copay ?? 0)}
                              {' '}
                              copay
                            </>
                          )
                          : 'Copay not set'}
                      </div>
                    </div>
                  )}
                  {isOutOfPocket && 'Out-of-pocket'}
                  {!hasInsurance && !isOutOfPocket && 'Not Set'}
                  <EditIcon className="!w-5 !h-5 !text-gray-700" />
                </div>
              )}
            </div>
          )}
        </div>
      )}
      <Popover
        anchorEl={container}
        classes={{
          paper: clsx(
            '!w-[640px] !mt-1',
            classes?.paper,
          ),
        }}
        onClose={() => {
          setContainer(null);
          onClose?.();
        }}
        disableEnforceFocus
      >
        <div className="populate-form">
          <Wrapper
            id="patient-insurance"
            label="Insurance"
            className="p-2"
          >
            <div className="flex w-full gap-2 items-center">
              <Controller
                name="insurance"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <PatientInsuranceSelect
                    patientId={patientId ?? 0}
                    classes={{ select: '!py-[3px] rounded' }}
                    insuranceId={(value as InsuranceDTO)?.insuranceId ?? 0}
                    onChange={(option) => {
                      onChange(option);
                      if (((option as InsuranceDTO)?.insuranceId ?? 0) !== 0) {
                        setValue('isOutOfPocket', false);
                      }
                      handleSubmit(onUpdate)();
                    }}
                    disabled={isLoading || readonly || modifyingAppointment}
                    editable={!readonly}
                  />
                )}
              />
              {!readonly && (
                <>
                  <Divider
                    orientation="vertical"
                    className="!text-xs font-semibold text-stone-400"
                  >
                    OR
                  </Divider>
                  <Controller
                    name="isOutOfPocket"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <OutOfPocketCheckbox
                        encounterId={encounterId ?? 0}
                        value={Boolean(value)}
                        disabled={shiftedToPatient}
                        shiftedToPatient={shiftedToPatient}
                        onChange={(checked: boolean) => {
                          onChange(checked);
                          if (checked) {
                            setValue('insurance', null);
                          }
                          handleSubmit(onUpdate)();
                        }}
                      />
                    )}
                  />
                </>
              )}
            </div>
          </Wrapper>
        </div>
      </Popover>
    </>
  );
}
